import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { CircularProgress, Typography, Box } from '@mui/material';

const DEFAULT_COLORS = [
  '#1565C0',
  '#2E7D32',
  '#E65100',
  '#6A1B9A',
  '#00695C',
  '#607D8B',
];

/**
 * Custom tooltip — shows count and percentage with coloured dots.
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        border: 'none',
        p: 1.5,
        minWidth: 160,
      }}
    >
      <Typography variant="body2" fontWeight={700} mb={0.75} sx={{ color: '#374151' }}>
        {label}
      </Typography>
      {payload.map((entry) => {
        const total = entry.payload.__total || 1;
        const rawValue = entry.payload[entry.name] ?? entry.payload[entry.dataKey];
        const pct = rawValue !== undefined
          ? ((rawValue / total) * 100).toFixed(1)
          : typeof entry.value === 'number'
          ? entry.value.toFixed(1)
          : entry.value;
        const count = rawValue !== undefined ? rawValue : '—';
        return (
          <Box key={entry.dataKey ?? entry.name} display="flex" alignItems="center" gap={1} mb={0.25}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: entry.fill || entry.color,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {entry.name}:&nbsp;
              <span style={{ fontWeight: 600, color: '#1a1a2e' }}>
                {count} ({pct}%)
              </span>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Custom legend — rounded colour dots.
 */
const CustomLegend = ({ payload }) => {
  if (!payload || payload.length === 0) return null;
  return (
    <Box display="flex" flexWrap="wrap" gap={1.5} justifyContent="center" mt={0.5}>
      {payload.map((entry) => (
        <Box key={entry.value} display="flex" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * DashboardStackedChart — 100% stacked bar chart.
 *
 * Props:
 *   data     — array of objects (each object should have a `total` for percentage calc)
 *   xKey     — string key for X axis
 *   series   — [{ dataKey, name, color }]
 *   title    — string
 *   height   — number (default 300)
 *   loading  — bool
 *   yLabel   — string (default 'Count')
 */
const DashboardStackedChart = ({
  data = [],
  xKey = 'year',
  series = [],
  title = '',
  height = 300,
  loading = false,
  yLabel = 'Count',
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  // Annotate each row with __total for the tooltip
  const enrichedData = data.map((row) => {
    const rowTotal = series.reduce((sum, s) => sum + (Number(row[s.dataKey]) || 0), 0);
    return { ...row, __total: rowTotal || 1 };
  });

  return (
    <Box>
      {title && (
        <Typography
          variant="subtitle2"
          fontWeight={700}
          mb={1.5}
          sx={{ color: '#374151' }}
        >
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={enrichedData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid stroke="#f0f0f0" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Legend content={<CustomLegend />} />
          {series.map((s, idx) => (
            <Bar
              key={s.dataKey}
              dataKey={(row) => {
                const total = row.__total || 1;
                return parseFloat(((Number(row[s.dataKey]) / total) * 100).toFixed(2));
              }}
              name={s.name || s.dataKey}
              stackId="stack"
              fill={s.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default DashboardStackedChart;
